import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { type ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

/**
 * A camada de dados dos tipos de multa e das bases de cálculo.
 *
 * Mesmo desenho das outras telas de parametrização: nenhum componente chama
 * `chamarApi` direto, chave de cache junto de quem a usa, `retry:
 * naoInsistirEmRecusa` em toda consulta (CLAUDE.md, "Regras de API").
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 * `/tipos-multa` e `/bases-calculo-multa` ainda não existem na API. As tabelas
 * `tipos_multa` e `bases_calculo_multa` existem no banco desde a migração
 * `0002_dominio_base.sql`, mas nenhuma rota as expõe. Decisão do dono de
 * 2026-08-31 (CLAUDE.md, "Proibido", exceção estendida às telas de configuração
 * e parametrização) — mesmas três condições de sempre: só leitura, dado
 * claramente fictício, removido antes de `develop` → `hml` (`git grep -n
 * "TEMPORÁRIO" apps/web/src`, checklist em `docs/ALVOS.md`). "Criar" continua
 * sem gravar nada — ver `TiposMultaPage.tsx`.
 */

/**
 * O fluxo que decide quem aprova a aplicação da multa.
 *
 * `fluxo_aprovacao` é `text` no banco (`0002_dominio_base.sql`, linha 3278), com
 * padrão `'FISCAL'` — não é tipo enumerado. O conjunto abaixo é o que o
 * protótipo oferecia, acrescido do padrão do banco.
 */
export const FLUXOS_DE_APROVACAO = ['AUTOMATICO', 'FISCAL', 'MANUAL', 'COMISSAO'] as const;

export type FluxoDeAprovacao = (typeof FLUXOS_DE_APROVACAO)[number];

export const ROTULO_DO_FLUXO: Record<FluxoDeAprovacao, string> = {
  AUTOMATICO: 'Automático',
  FISCAL: 'Fiscal',
  MANUAL: 'Manual',
  COMISSAO: 'Comissão',
};

/** Uma base de cálculo, como a API (ainda inexistente) devolveria. */
export interface BaseDeCalculo {
  codigo: string;
  nome: string;
  formula_descritiva: string | null;
}

/**
 * Um tipo de multa, como a API (ainda inexistente) devolveria.
 *
 * `percentual` e `valor_fixo` são `numeric` no banco, entregues como string
 * decimal. Um tipo usa um ou outro: percentual sobre a base de cálculo, ou valor
 * fixo em reais.
 */
export interface TipoDeMulta {
  id: string;
  codigo: string;
  descricao: string;
  percentual: number | string | null;
  valor_fixo: number | string | null;
  base_calculo: string;
  fluxo_aprovacao: FluxoDeAprovacao;
  vigencia_inicio: string | null;
  vigencia_fim: string | null;
  ativo: boolean;
}

export const CHAVES_DE_TIPOS_DE_MULTA = {
  tipos: ['tipos-multa', 'lista'] as const,
  bases: ['bases-calculo-multa', 'ativas'] as const,
};

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
const BASES_MOCK: BaseDeCalculo[] = [
  {
    codigo: 'VALOR_MENSAL',
    nome: 'Valor mensal da ordem de serviço',
    formula_descritiva: 'Percentual sobre o valor mensal contratado da OS.',
  },
  {
    codigo: 'VALOR_DIARIO',
    nome: 'Valor diário da ordem de serviço',
    formula_descritiva: 'Percentual sobre o valor mensal dividido pelos dias letivos.',
  },
  {
    codigo: 'VALOR_MEDICAO',
    nome: 'Valor da medição da competência',
    formula_descritiva: 'Percentual sobre o valor apurado na competência da ocorrência.',
  },
  {
    codigo: 'VALOR_FIXO',
    nome: 'Valor fixo em reais',
    formula_descritiva: 'Não usa percentual; aplica o valor fixo cadastrado no tipo.',
  },
];

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Cinco tipos cobrindo os ramos que a tela precisa mostrar: percentual e valor
// fixo, os quatro fluxos de aprovação, vigência aberta e encerrada, ativo e
// inativo. Códigos e percentuais são de exemplo — nada aqui foi apurado com a
// SMT nem sai de norma publicada.
const TIPOS_MOCK: TipoDeMulta[] = [
  {
    id: 'tipo-multa-exemplo-01',
    codigo: 'ATRASO_REITERADO',
    descricao: 'Atraso reiterado no cumprimento do itinerário',
    percentual: '5.0',
    valor_fixo: null,
    base_calculo: 'VALOR_DIARIO',
    fluxo_aprovacao: 'AUTOMATICO',
    vigencia_inicio: '2026-01-01',
    vigencia_fim: null,
    ativo: true,
  },
  {
    id: 'tipo-multa-exemplo-02',
    codigo: 'VEICULO_SEM_VISTORIA',
    descricao: 'Veículo em operação com vistoria vencida',
    percentual: '10.0',
    valor_fixo: null,
    base_calculo: 'VALOR_MENSAL',
    fluxo_aprovacao: 'FISCAL',
    vigencia_inicio: '2026-01-01',
    vigencia_fim: null,
    ativo: true,
  },
  {
    id: 'tipo-multa-exemplo-03',
    codigo: 'CONDUTOR_NAO_CREDENCIADO',
    descricao: 'Condução por profissional sem credenciamento ativo',
    percentual: null,
    valor_fixo: '1500.00',
    base_calculo: 'VALOR_FIXO',
    fluxo_aprovacao: 'COMISSAO',
    vigencia_inicio: '2026-01-01',
    vigencia_fim: null,
    ativo: true,
  },
  {
    id: 'tipo-multa-exemplo-04',
    codigo: 'RECUSA_VISTORIA',
    descricao: 'Recusa em apresentar veículo para vistoria da fiscalização',
    percentual: '15.0',
    valor_fixo: null,
    base_calculo: 'VALOR_MEDICAO',
    fluxo_aprovacao: 'MANUAL',
    vigencia_inicio: '2026-02-01',
    vigencia_fim: null,
    ativo: true,
  },
  {
    id: 'tipo-multa-exemplo-05',
    codigo: 'MONITOR_AUSENTE',
    descricao: 'Operação sem monitor em rota que o exige',
    percentual: '7.5',
    valor_fixo: null,
    base_calculo: 'VALOR_DIARIO',
    fluxo_aprovacao: 'FISCAL',
    vigencia_inicio: '2025-01-01',
    vigencia_fim: '2025-12-31',
    ativo: false,
  },
];

/**
 * Os tipos de multa cadastrados.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 */
export function useTiposDeMulta(): UseQueryResult<TipoDeMulta[], ErroApi> {
  return useQuery<TipoDeMulta[], ErroApi>({
    queryKey: CHAVES_DE_TIPOS_DE_MULTA.tipos,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => TIPOS_MOCK,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * As bases de cálculo ativas, para o campo de escolha do formulário.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 */
export function useBasesDeCalculo(): UseQueryResult<BaseDeCalculo[], ErroApi> {
  return useQuery<BaseDeCalculo[], ErroApi>({
    queryKey: CHAVES_DE_TIPOS_DE_MULTA.bases,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => BASES_MOCK,
    retry: naoInsistirEmRecusa,
  });
}

/** O nome de exibição de uma base de cálculo, com recuo para o próprio código. */
export function nomeDaBase(codigo: string, bases: BaseDeCalculo[] | undefined): string {
  return bases?.find((base) => base.codigo === codigo)?.nome ?? codigo.replace(/_/g, ' ');
}
